// src/app/tools/color-palette/page.tsx
'use client';

import { useState, useEffect } from 'react';
import { Copy, RefreshCw, Palette, Check, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { HexColorPicker } from 'react-colorful';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { colorPaletteContent } from '../../data/color-palette-content';
import Link from 'next/link';

type ColorShade = {
    name: string;
    value: string;
    textColor: string;
};

export default function ColorPaletteGenerator() {
    const [baseColor, setBaseColor] = useState('#3b82f6'); // Default blue-500
    const [palette, setPalette] = useState<ColorShade[]>([]);
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showHexValues, setShowHexValues] = useState(true);

    // Generate color palette from base color
    useEffect(() => {
        if (baseColor) {
            generatePalette(baseColor);
        }
    }, [baseColor]);

    const generatePalette = (color: string) => {
        try {
            const colors = generateColorShades(color);
            setPalette(colors);
        } catch (error) {
            console.error('Error generating palette:', error);
            toast.error('Failed to generate color palette');
        }
    };

    const generateColorShades = (hexColor: string): ColorShade[] => {
        // Convert hex to RGB
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        const shades: ColorShade[] = [
            { name: '50', value: lightenColor(r, g, b, 0.9), textColor: getContrastColor(lightenColor(r, g, b, 0.9)) },
            { name: '100', value: lightenColor(r, g, b, 0.7), textColor: getContrastColor(lightenColor(r, g, b, 0.7)) },
            { name: '200', value: lightenColor(r, g, b, 0.5), textColor: getContrastColor(lightenColor(r, g, b, 0.5)) },
            { name: '300', value: lightenColor(r, g, b, 0.3), textColor: getContrastColor(lightenColor(r, g, b, 0.3)) },
            { name: '400', value: lightenColor(r, g, b, 0.1), textColor: getContrastColor(lightenColor(r, g, b, 0.1)) },
            { name: '500', value: `#${hex}`, textColor: getContrastColor(`#${hex}`) },
            { name: '600', value: darkenColor(r, g, b, 0.1), textColor: getContrastColor(darkenColor(r, g, b, 0.1)) },
            { name: '700', value: darkenColor(r, g, b, 0.3), textColor: getContrastColor(darkenColor(r, g, b, 0.3)) },
            { name: '800', value: darkenColor(r, g, b, 0.5), textColor: getContrastColor(darkenColor(r, g, b, 0.5)) },
            { name: '900', value: darkenColor(r, g, b, 0.7), textColor: getContrastColor(darkenColor(r, g, b, 0.7)) },
            { name: '950', value: darkenColor(r, g, b, 0.9), textColor: getContrastColor(darkenColor(r, g, b, 0.9)) },
        ];

        return shades;
    };

    // Helper functions for color manipulation
    const lightenColor = (r: number, g: number, b: number, amount: number): string => {
        const newR = Math.min(255, r + (255 - r) * amount);
        const newG = Math.min(255, g + (255 - g) * amount);
        const newB = Math.min(255, b + (255 - b) * amount);
        return `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG).toString(16).padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`;
    };

    const darkenColor = (r: number, g: number, b: number, amount: number): string => {
        const newR = Math.max(0, r * (1 - amount));
        const newG = Math.max(0, g * (1 - amount));
        const newB = Math.max(0, b * (1 - amount));
        return `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG).toString(16).padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`;
    };

    const getContrastColor = (hexColor: string): string => {
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 128 ? '#000000' : '#ffffff';
    };

    const copyToClipboard = async (color: string, index: number) => {
        try {
            await navigator.clipboard.writeText(color);
            setCopiedIndex(index);
            toast.success(`Copied ${color} to clipboard`);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
            toast.error('Failed to copy to clipboard');
        }
    };

    const getRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        setBaseColor(color);
    };

    // Helper function to convert hex to RGB
    const hexToRgb = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return 'Invalid color';
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `${r}, ${g}, ${b}`;
    };

    // Helper function to convert hex to HSL
    const hexToHsl = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return 'Invalid color';

        let r = parseInt(result[1], 16) / 255;
        let g = parseInt(result[2], 16) / 255;
        let b = parseInt(result[3], 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0;
        let s = 0;
        let l = (max + min) / 2;

        if (max === min) {
            h = s = 0; // achromatic
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }

        h = Math.round(h * 360);
        s = Math.round(s * 100);
        l = Math.round(l * 100);

        return `${h}, ${s}%, ${l}%`;
    };

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6">
            <div className="mb-8">
                <ToolPageIntro title="Color Palette Generator">
                Generate beautiful color palettes with our free online tool. Create harmonious color schemes for web design, branding, and creative projects.
            </ToolPageIntro>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Color Picker Section */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-medium text-gray-900">Color Picker</h2>
                            <div className="flex space-x-2">
                                <button
                                    onClick={getRandomColor}
                                    className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
                                    title="Random Color"
                                >
                                    <RefreshCw className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => setShowHexValues(!showHexValues)}
                                    className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
                                    title={showHexValues ? 'Hide Hex Values' : 'Show Hex Values'}
                                >
                                    {showHexValues ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>
                        <div className="relative">
                            <div
                                className="w-full h-12 rounded-md cursor-pointer mb-4 border border-gray-300"
                                style={{ backgroundColor: baseColor }}
                                onClick={() => setShowColorPicker(!showColorPicker)}
                            />

                            {showColorPicker && (
                                <div className="absolute z-50 bg-white p-4 rounded-lg shadow-xl border border-gray-200"
                                    style={{ top: '100%', left: 0, width: '300px' }}>
                                    <HexColorPicker color={baseColor} onChange={setBaseColor} className="w-full" />
                                    <div className="mt-3 flex items-center">
                                        <input
                                            type="text"
                                            value={baseColor}
                                            onChange={(e) => setBaseColor(e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowColorPicker(false);
                                            }}
                                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Color Information</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">HEX:</span>
                                    <div className="flex items-center">
                                        <span className="font-mono text-sm">{baseColor.toUpperCase()}</span>
                                        <button
                                            onClick={() => copyToClipboard(baseColor.toUpperCase(), -1)}
                                            className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                                        >
                                            {copiedIndex === -1 ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">RGB:</span>
                                    <span className="text-sm text-gray-700">
                                        {hexToRgb(baseColor)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">HSL:</span>
                                    <span className="text-sm text-gray-700">
                                        {hexToHsl(baseColor)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">How to use</h3>
                        <ol className="text-sm text-gray-600 space-y-2 list-decimal pl-5">
                            <li>Pick a base color using the color picker or enter a hex code</li>
                            <li>Copy any color by clicking on it</li>
                            <li>Use the random button to discover new color combinations</li>
                            <li>Toggle hex values on/off for better visualization</li>
                        </ol>
                    </div>
                </div>

                {/* Color Palette */}
                <div className="lg:col-span-2">
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-medium text-gray-900">Color Palette</h2>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => {
                                        const colors = palette.map(c => c.value).join('\n');
                                        navigator.clipboard.writeText(colors);
                                        toast.success('All colors copied to clipboard');
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                >
                                    <Copy className="h-4 w-4 mr-1" />
                                    Copy All
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            {palette.map((shade, index) => (
                                <div
                                    key={shade.name}
                                    className="group flex items-center h-12 rounded-md overflow-hidden hover:shadow-md transition-shadow"
                                    style={{ backgroundColor: shade.value }}
                                >
                                    <div
                                        className="w-1/4 h-full flex items-center justify-between px-4 cursor-pointer"
                                        onClick={() => copyToClipboard(shade.value, index)}
                                    >
                                        <span
                                            className="font-medium"
                                            style={{ color: shade.textColor }}
                                        >
                                            {shade.name}
                                        </span>
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                            {copiedIndex === index ? (
                                                <Check className="h-4 w-4" style={{ color: shade.textColor }} />
                                            ) : (
                                                <Copy className="h-4 w-4" style={{ color: shade.textColor }} />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-1 h-full flex items-center justify-between px-4">
                                        {showHexValues && (
                                            <span
                                                className="font-mono text-sm"
                                                style={{ color: shade.textColor }}
                                            >
                                                {shade.value.toUpperCase()}
                                            </span>
                                        )}
                                        <span
                                            className="text-sm opacity-70"
                                            style={{ color: shade.textColor }}
                                        >
                                            {Math.round(parseInt(shade.value.replace('#', ''), 16) / 16777215 * 100)}%
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-6 bg-white p-6 rounded-lg border border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">CSS Variables</h2>
                        <div className="bg-gray-50 p-4 rounded-md overflow-x-auto">
                            <pre className="text-sm text-gray-800">
                                <code>
                                    {`:root {\n${palette
                                        .map(
                                            (shade) =>
                                                `  --color-${shade.name}: ${shade.value.toLowerCase()};`
                                        )
                                        .join('\n')}\n}`}
                                </code>
                            </pre>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => {
                                    const cssVars = `:root {\n${palette
                                        .map(
                                            (shade) =>
                                                `  --color-${shade.name}: ${shade.value.toLowerCase()};`
                                        )
                                        .join('\n')}\n}`;
                                    navigator.clipboard.writeText(cssVars);
                                    toast.success('CSS variables copied to clipboard');
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                            >
                                <Copy className="h-4 w-4 mr-1" />
                                Copy CSS
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={colorPaletteContent.title}
                    description={colorPaletteContent.description}
                    content={colorPaletteContent.content}
                    faqs={colorPaletteContent.faqs}
                    relatedTools={colorPaletteContent.relatedTools}
                />
            </div>
        </div>
    );
}